import { ClipboardPaste } from 'lucide-react';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button/Button';
import { cn } from '@/shared/utils/cn/cn.util';

import { KeyValueAddControl } from '../KeyValueAddControl/KeyValueAddControl';
import { KeyValueImportDialog } from '../KeyValueImportDialog/KeyValueImportDialog';
import { KeyValueRow } from '../KeyValueRow/KeyValueRow';
import { StructuredShell } from '../StructuredShell/StructuredShell';
import {
  KEY_VALUE_EMPTY_CLASSES,
  KEY_VALUE_FOOTER_CLASSES,
  KEY_VALUE_LIST_CLASSES,
} from '../structured.tokens';

import { type KeyValueEditorHandle, type KeyValueEditorProps } from './KeyValueEditor.types';
import { useKeyValueEditorLogic } from './useKeyValueEditorLogic';

/**
 * A record of `key → value`, edited as rows.
 *
 * The whole point of the v2 is that it stops **guessing**. Where the caller can
 * describe the keys, the key is chosen rather than typed and the value is drawn
 * in the control its kind calls for — a switch is a switch, a closed set is a
 * select, a byte ceiling is a number with its bounds. Where the caller cannot,
 * it is the same two boxes as before, but with a duplicate that refuses to emit,
 * a number that refuses to become a silent `0`, and a stored value of the wrong
 * type kept exactly as it is rather than rewritten.
 *
 * All the behaviour is in `useKeyValueEditorLogic` (U5); this file is the
 * arrangement of it.
 */
export const KeyValueEditor = forwardRef<KeyValueEditorHandle, KeyValueEditorProps>(
  function KeyValueEditor(
    {
      id,
      className,
      size = 'md',
      label,
      'aria-describedby': describedBy,
      isDisabled = false,
      isReadOnly = false,
      isInvalid = false,
      onBlur,
      emptyContent,
      keyPlaceholder,
      valuePlaceholder,
      valueDir,
      columns = 'balanced',
      options,
      maxRows,
      isImportable = false,
      ...rest
    },
    ref,
  ) {
    const { t } = useTranslation('uiKit');
    const container = useRef<HTMLDivElement | null>(null);
    const logic = useKeyValueEditorLogic({ ...rest, maxRows, isDisabled, isReadOnly });

    // Not an element handle: there is no node that IS this control, and a
    // focusable wrapper would satisfy `shouldFocusError` while leaving the
    // operator one Tab short of the row that is actually wrong.
    useImperativeHandle(ref, () => ({ focus: logic.focus, element: container.current }), [
      logic.focus,
    ]);

    const canEdit = !isDisabled && !isReadOnly;

    return (
      <StructuredShell
        role="group"
        id={id}
        className={className}
        label={label}
        aria-describedby={describedBy}
        isReadOnly={isReadOnly}
        announcement={logic.announcement}
        toolbar={
          isImportable && canEdit ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              startIcon={<ClipboardPaste aria-hidden className="h-4 w-4" />}
              onClick={logic.import.open}
            >
              {t('keyValue.import.action')}
            </Button>
          ) : undefined
        }
      >
        <div ref={container} aria-invalid={isInvalid ? true : undefined}>
          {logic.rows.length === 0 ? (
            (emptyContent ?? <p className={KEY_VALUE_EMPTY_CLASSES}>{t('keyValue.empty')}</p>)
          ) : (
            <ul className={cn(KEY_VALUE_LIST_CLASSES)}>
              {logic.rows.map((row, index) => (
                <KeyValueRow
                  key={row.rowId}
                  row={row}
                  index={index}
                  size={size}
                  columns={columns}
                  options={options}
                  keyPlaceholder={keyPlaceholder}
                  valuePlaceholder={valuePlaceholder}
                  valueDir={valueDir}
                  isDisabled={isDisabled}
                  isReadOnly={isReadOnly}
                  onBlur={onBlur}
                  registerControl={(element) => {
                    logic.registerControl(row.rowId, element);
                  }}
                  onKeyChange={(key) => {
                    logic.setKey(row.rowId, key);
                  }}
                  onTextChange={(text) => {
                    logic.setText(row.rowId, text);
                  }}
                  onBooleanChange={(isOn) => {
                    logic.setBoolean(row.rowId, isOn);
                  }}
                  onAdoptKind={() => {
                    logic.adoptKind(row.rowId);
                  }}
                  onRemove={() => {
                    logic.removeRow(row.rowId);
                  }}
                />
              ))}
            </ul>
          )}
        </div>

        {canEdit ? (
          <div className={KEY_VALUE_FOOTER_CLASSES}>
            {logic.canAdd ? (
              <KeyValueAddControl
                remaining={logic.remaining}
                hasCatalogue={logic.hasCatalogue}
                canAddCustom={logic.canAddCustom}
                isDisabled={isDisabled}
                registerControl={logic.registerAddControl}
                onAdd={logic.addRow}
              />
            ) : (
              // The ceiling is stated where the affordance was, rather than
              // leaving a control that refuses on click.
              <p className={KEY_VALUE_EMPTY_CLASSES}>
                {t('keyValue.maxRows', { count: maxRows ?? 0 })}
              </p>
            )}
          </div>
        ) : null}

        {isImportable && canEdit ? (
          <KeyValueImportDialog importer={logic.import} onApplied={logic.announceImported} />
        ) : null}
      </StructuredShell>
    );
  },
);
